import { CommonModule, DatePipe } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { ChipModule } from 'primeng/chip';
import { DialogModule } from 'primeng/dialog';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { PageDataPanelComponent } from '../shared/page-layout/page-data-panel.component';
import { PageFormSectionComponent } from '../shared/page-layout/page-form-section.component';
import { PageHeroComponent } from '../shared/page-layout/page-hero.component';
import { PageShellComponent } from '../shared/page-layout/page-shell.component';
import { Customer } from '../customer/customer.model';
import { CustomerService } from '../customer/customer.service';
import { ProductivityIncident } from './productivity.model';
import { ProductivityService } from './productivity.service';

interface ResourceOption {
  id: number;
  resourceKey: string;
  label: string;
}

@Component({
  selector: 'app-productivity-incidents',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, DatePipe, ButtonModule, ChipModule, DialogModule, IftaLabelModule, InputGroupModule, InputGroupAddonModule, InputNumberModule, InputTextModule, SelectModule, TableModule, TagModule, TextareaModule, PageShellComponent, PageHeroComponent, PageDataPanelComponent, PageFormSectionComponent],
  template: `
    <app-page-shell>
      <app-page-hero
        eyebrow="Workspace"
        title="Incidents"
        subtitle="Capture operational issues, monitor severity, and manage resolution work.">
        <div nav class="incident-filters">
          <p-select [options]="statusOptionsWithAll" [(ngModel)]="statusFilter" optionLabel="label" optionValue="value" placeholder="Status" (onChange)="load()"></p-select>
          <p-select [options]="severityOptionsWithAll" [(ngModel)]="severityFilter" optionLabel="label" optionValue="value" placeholder="Severity" (onChange)="load()"></p-select>
        </div>
        <div actions>
          <p-button label="New Incident" icon="pi pi-plus" (onClick)="openIncident()"></p-button>
        </div>
      </app-page-hero>

      <app-page-data-panel title="Incident Register" subtitle="Open each incident for linked resolution tasks and closure notes." [resultText]="incidents.length + ' incidents'">
        <p-table [value]="incidents" [loading]="loading" [paginator]="true" [rows]="15" styleClass="workspace-table">
          <ng-template pTemplate="header">
            <tr><th>Incident</th><th>Owner</th><th>Severity</th><th>Status</th><th>Updated</th><th class="text-right">Actions</th></tr>
          </ng-template>
          <ng-template pTemplate="body" let-incident>
            <tr>
              <td>
                <div class="font-semibold">{{ incident.title }}</div>
                <div class="text-sm text-500">{{ incident.description }}</div>
                <div class="text-xs text-500 mt-1" *ngIf="incident.resourceKey">{{ incident.resourceKey }} #{{ incident.resourceId }}</div>
              </td>
              <td>
                <p-chip *ngIf="incident.ownerCustomer || incident.ownerCustomerId; else noOwner"
                  icon="pi pi-user"
                  [label]="customerLabel(incident.ownerCustomer, incident.ownerCustomerId)"
                  styleClass="workspace-customer-chip">
                </p-chip>
                <ng-template #noOwner><span class="text-500">-</span></ng-template>
              </td>
              <td><p-tag [value]="incident.severity || 'MEDIUM'" [severity]="severityTag(incident.severity)"></p-tag></td>
              <td><p-tag [value]="incident.status || 'OPEN'"></p-tag></td>
              <td>{{ incident.updatedAt | date:'medium' }}</td>
              <td class="text-right">
                <p-button icon="pi pi-eye" [text]="true" [routerLink]="['/productivity/incidents', incident.id]"></p-button>
                <p-button icon="pi pi-pencil" [text]="true" (onClick)="openIncident(incident)"></p-button>
              </td>
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage">
            <tr><td colspan="6" class="text-center p-4 text-500">No incidents found.</td></tr>
          </ng-template>
        </p-table>
      </app-page-data-panel>

      <p-dialog header="Incident" [(visible)]="dialogVisible" [modal]="true" styleClass="workspace-dialog" [style]="{width: '50rem'}">
        <app-page-form-section icon="pi pi-exclamation-triangle" title="Incident Details" subtitle="Capture the issue, severity, owner, and linked operational resource.">
          <div class="dialog-form-grid">
            <div class="dialog-field span-2">
              <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-exclamation-triangle"></i></p-inputGroupAddon>
                <p-iftaLabel>
                  <input id="incidentTitle" pInputText [(ngModel)]="draft.title">
                  <label for="incidentTitle">Title</label>
                </p-iftaLabel>
              </p-inputGroup>
            </div>
            <div class="dialog-field span-2">
              <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-align-left"></i></p-inputGroupAddon>
                <p-iftaLabel>
                  <textarea id="incidentDescription" pTextarea rows="3" [(ngModel)]="draft.description"></textarea>
                  <label for="incidentDescription">Description</label>
                </p-iftaLabel>
              </p-inputGroup>
            </div>
            <div class="dialog-field">
              <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-tag"></i></p-inputGroupAddon>
                <p-iftaLabel>
                  <input id="incidentCategory" pInputText [(ngModel)]="draft.category">
                  <label for="incidentCategory">Category</label>
                </p-iftaLabel>
              </p-inputGroup>
            </div>
            <div class="dialog-field">
              <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-user"></i></p-inputGroupAddon>
                <p-iftaLabel>
                  <p-select inputId="incidentOwner" [options]="customerOptions" [ngModel]="selectedOwner"
                    (ngModelChange)="onOwnerChange($event)" optionLabel="name" [filter]="true" [lazy]="true"
                    (onShow)="loadCustomerOptions()" (onLazyLoad)="loadCustomerOptions($event)" appendTo="body" styleClass="w-full">
                    <ng-template pTemplate="item" let-customer>
                      <div class="option-stack"><strong>{{ customer.name || ('Customer #' + customer.id) }}</strong><small>{{ customer.emailAddress || customer.phoneNumber || ('ID ' + customer.id) }}</small></div>
                    </ng-template>
                    <ng-template pTemplate="selectedItem" let-customer>
                      <span>{{ customer?.name || (customer?.id ? ('Customer #' + customer.id) : '') }}</span>
                    </ng-template>
                  </p-select>
                  <label for="incidentOwner">Owner</label>
                </p-iftaLabel>
              </p-inputGroup>
            </div>
            <div class="dialog-field">
              <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-bolt"></i></p-inputGroupAddon>
                <p-iftaLabel>
                  <p-select inputId="incidentSeverity" [options]="severityOptions" [(ngModel)]="draft.severity" optionLabel="label" optionValue="value" appendTo="body" styleClass="w-full"></p-select>
                  <label for="incidentSeverity">Severity</label>
                </p-iftaLabel>
              </p-inputGroup>
            </div>
            <div class="dialog-field">
              <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-circle"></i></p-inputGroupAddon>
                <p-iftaLabel>
                  <p-select inputId="incidentStatus" [options]="statusOptions" [(ngModel)]="draft.status" optionLabel="label" optionValue="value" appendTo="body" styleClass="w-full"></p-select>
                  <label for="incidentStatus">Status</label>
                </p-iftaLabel>
              </p-inputGroup>
            </div>
            <div class="dialog-field">
              <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-sitemap"></i></p-inputGroupAddon>
                <p-iftaLabel>
                  <p-select inputId="incidentResourceKey" [options]="resourceTypeOptions" [(ngModel)]="draft.resourceKey"
                    optionLabel="label" optionValue="value" [filter]="true" appendTo="body" styleClass="w-full"
                    (onChange)="onResourceKeyChange()"></p-select>
                  <label for="incidentResourceKey">Resource type</label>
                </p-iftaLabel>
              </p-inputGroup>
            </div>
            <div class="dialog-field">
              <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-hashtag"></i></p-inputGroupAddon>
                <p-iftaLabel>
                  <p-select inputId="incidentResource" [options]="resourceOptions" [ngModel]="selectedResource"
                    (ngModelChange)="onResourceChange($event)" optionLabel="label" [filter]="true" [lazy]="true"
                    (onShow)="loadResourceOptions()" (onLazyLoad)="loadResourceOptions($event)" appendTo="body" styleClass="w-full"></p-select>
                  <label for="incidentResource">Resource</label>
                </p-iftaLabel>
              </p-inputGroup>
            </div>
          </div>
        </app-page-form-section>
        <ng-template pTemplate="footer">
          <p-button label="Cancel" severity="secondary" (onClick)="dialogVisible = false"></p-button>
          <p-button label="Save" icon="pi pi-check" (onClick)="saveIncident()"></p-button>
        </ng-template>
      </p-dialog>
    </app-page-shell>
  `,
  styles: [`
    :host { display: block; }
    .incident-filters { display: flex; flex-wrap: wrap; gap: 0.5rem; }
    :host ::ng-deep .workspace-table .p-datatable-table { min-width: 64rem; }
    .dialog-form-grid {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 1rem;
    }
    .dialog-field {
      display: flex;
      flex-direction: column;
      gap: 0.45rem;
      min-width: 0;
      color: #334155;
      font-weight: 600;
    }
    .dialog-field > span {
      font-size: 0.85rem;
    }
    .span-2 {
      grid-column: 1 / -1;
    }
    :host ::ng-deep .dialog-field .p-inputtext,
    :host ::ng-deep .dialog-field .p-inputnumber,
    :host ::ng-deep .dialog-field .p-inputnumber-input,
    :host ::ng-deep .dialog-field .p-select,
    .dialog-field input,
    .dialog-field textarea {
      width: 100%;
    }
    @media (max-width: 640px) {
      .dialog-form-grid { grid-template-columns: 1fr; }
      .span-2 { grid-column: auto; }
    }
  `]
})
export class ProductivityIncidentsComponent implements OnInit {
  private service = inject(ProductivityService);
  private router = inject(Router);
  private customerService = inject(CustomerService);
  incidents: ProductivityIncident[] = [];
  draft: ProductivityIncident = { title: '', severity: 'MEDIUM', status: 'OPEN' };
  customerOptions: Customer[] = [];
  selectedOwner: Customer | null = null;
  resourceOptions: ResourceOption[] = [];
  selectedResource: ResourceOption | null = null;
  loading = false;
  dialogVisible = false;
  statusFilter?: string;
  severityFilter?: string;
  statusOptions = ['OPEN', 'INVESTIGATING', 'BLOCKED', 'RESOLVED', 'CLOSED'].map(value => ({ label: value, value }));
  severityOptions = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'].map(value => ({ label: value, value }));
  statusOptionsWithAll = [{ label: 'All Statuses', value: undefined }, ...this.statusOptions];
  severityOptionsWithAll = [{ label: 'All Severities', value: undefined }, ...this.severityOptions];
  resourceTypeOptions = [
    { label: 'Shipment Delivery', value: 'SHIPMENT_DELIVERY' },
    { label: 'Customs Clearance', value: 'CUSTOMS_CLEARANCE' },
    { label: 'Trip Costing', value: 'TRIP_COSTING' },
    { label: 'Warehouse Transfer', value: 'WAREHOUSE_TRANSFER' },
    { label: 'Fleet Mobilization', value: 'FLEET_MOBILIZATION' },
    { label: 'Accounts Receivable', value: 'ACCOUNTS_RECEIVABLE' },
    { label: 'Productivity Summary', value: 'PRODUCTIVITY_SUMMARY' },
    { label: 'Productivity Incident', value: 'PRODUCTIVITY_INCIDENT' }
  ];

  ngOnInit(): void { this.load(); }

  load(): void {
    this.loading = true;
    this.service.getIncidents({ status: this.statusFilter, severity: this.severityFilter }).subscribe({
      next: res => { this.incidents = res.data || []; this.loading = false; },
      error: () => this.loading = false
    });
  }

  openIncident(incident?: ProductivityIncident): void {
    this.draft = incident ? { ...incident } : { title: '', severity: 'MEDIUM', status: 'OPEN', ownerCustomerId: this.service.getCustomerId() };
    this.syncDialogSelections();
    this.dialogVisible = true;
  }

  saveIncident(): void {
    this.service.saveIncident(this.draft).subscribe(res => {
      this.dialogVisible = false;
      this.service.refreshSummary();
      this.load();
      if (!this.draft.id && res.data?.id) this.router.navigate(['/productivity/incidents', res.data.id]);
    });
  }

  severityTag(severity?: string): 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' {
    if (severity === 'CRITICAL') return 'danger';
    if (severity === 'HIGH') return 'warn';
    if (severity === 'LOW') return 'secondary';
    return 'info';
  }

  customerLabel(customer?: Customer, fallbackId?: number): string {
    return customer?.name || customer?.emailAddress || (fallbackId ? `Customer #${fallbackId}` : '-');
  }

  loadCustomerOptions(event?: any): void {
    this.customerService.getCustomersByCompany(this.service.getCompanyId()).subscribe(customers => {
      const search = String(event?.filter || '').toLowerCase();
      this.customerOptions = (customers || []).filter(customer => {
        const label = `${customer.name || ''} ${customer.emailAddress || ''} ${customer.phoneNumber || ''} ${customer.id || ''}`.toLowerCase();
        return !search || label.includes(search);
      });
      this.syncOwnerSelection();
    });
  }

  loadResourceOptions(event?: any): void {
    const search = String(event?.filter || '').toLowerCase();
    const options = new Map<string, ResourceOption>();
    const addOption = (resourceKey?: string, resourceId?: number) => {
      if (!resourceKey || !resourceId) return;
      const option: ResourceOption = { id: resourceId, resourceKey, label: `${this.resourceTypeLabel(resourceKey)} #${resourceId}` };
      options.set(`${resourceKey}:${resourceId}`, option);
    };

    this.incidents.forEach(incident => addOption(incident.resourceKey, incident.resourceId));
    addOption(this.draft.resourceKey, this.draft.resourceId);

    this.resourceOptions = Array.from(options.values())
      .filter(option => (!this.draft.resourceKey || option.resourceKey === this.draft.resourceKey)
        && (!search || option.label.toLowerCase().includes(search)));
    this.syncResourceSelection();
  }

  onOwnerChange(customer: Customer | null): void {
    this.selectedOwner = customer;
    this.draft.ownerCustomerId = customer?.id;
  }

  onResourceKeyChange(): void {
    this.draft.resourceId = undefined;
    this.selectedResource = null;
    this.loadResourceOptions();
  }

  onResourceChange(resource: ResourceOption | null): void {
    this.selectedResource = resource;
    this.draft.resourceKey = resource?.resourceKey || this.draft.resourceKey;
    this.draft.resourceId = resource?.id;
  }

  private syncDialogSelections(): void {
    this.syncOwnerSelection();
    this.loadResourceOptions();
  }

  private syncOwnerSelection(): void {
    const id = this.draft.ownerCustomerId;
    this.selectedOwner = id
      ? this.customerOptions.find(customer => customer.id === id) || ({ id, name: `Customer #${id}` } as Customer)
      : null;
  }

  private syncResourceSelection(): void {
    const key = this.draft.resourceKey;
    const id = this.draft.resourceId;
    this.selectedResource = key && id
      ? this.resourceOptions.find(option => option.resourceKey === key && option.id === id) || { id, resourceKey: key, label: `${this.resourceTypeLabel(key)} #${id}` }
      : null;
  }

  private resourceTypeLabel(resourceKey: string): string {
    return this.resourceTypeOptions.find(option => option.value === resourceKey)?.label || resourceKey.replace(/_/g, ' ');
  }
}
